import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FaRobot, FaPlus, FaExclamationTriangle,
  FaStethoscope, FaCar, FaMoneyBill, FaGraduationCap, FaPlane, FaShoppingCart,
  FaHome, FaUtensils, FaDumbbell, FaSpa, FaTruck, FaBalanceScale, FaFilm,
  FaTshirt, FaSeedling, FaGamepad, FaUsers, FaShieldAlt, FaBroadcastTower,
  FaBitcoin, FaBook, FaTicketAlt, FaCamera, FaMusic, FaIndustry, FaBolt,
  FaInfoCircle, FaEdit, FaComments, FaChartBar, FaCheckCircle, FaEllipsisV,
  FaCog, FaQuestionCircle, FaPaperPlane, FaUserFriends, FaStopwatch,
  FaUpload, FaTimes, FaTrashAlt, FaStar
} from "react-icons/fa";
import { FiGrid, FiTrendingUp, FiDollarSign, FiSettings, FiLogOut, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { getApiBase } from "../apiBase";
import { motion, AnimatePresence } from "framer-motion";
import { logEvent } from "../utils/logger";
import Billing from "./Billing";
import HeaderActions from "../components/HeaderActions";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import Sidebar from "../components/Sidebar";

/* ==========================================
   DESIGN TOKENS (matches Billing History)
   Plum:  #3D2542 (ink)  #5B3A60 (brand)  #4A2E50 (brand-dark)  #F0E6F5 (tint)  #FAF8FB (page bg)
   Gold:  #B89D4F (accent)  #A88D3F (accent-dark)
   Line:  #E4DCE6
   Text:  #7A6B7E (muted)
   State: success #1F9D6C / #E8F7F0 / border #BFE8D5   error #C62828 / #FDEEEE / border #F0C7C7
   ========================================== */

const DEFAULT_AVATARS = [
  { label: "Business", url: "https://cdn-icons-png.flaticon.com/512/4140/4140048.png" },
  { label: "Doctor", url: "https://cdn-icons-png.flaticon.com/512/3774/3774299.png" },
  { label: "Finance", url: "https://cdn-icons-png.flaticon.com/512/2331/2331941.png" },
  { label: "Automobile", url: "https://cdn-icons-png.flaticon.com/512/743/743131.png" },
  { label: "Fashion", url: "https://cdn-icons-png.flaticon.com/512/2922/2922561.png" },
  { label: "Technology", url: "https://cdn-icons-png.flaticon.com/512/4140/4140061.png" },
  { label: "Education", url: "https://cdn-icons-png.flaticon.com/512/1995/1995574.png" },
  { label: "Retail", url: "https://cdn-icons-png.flaticon.com/512/3081/3081559.png" },
  { label: "Support", url: "https://cdn-icons-png.flaticon.com/512/4140/4140037.png" }
];

/* ---------------------------------------------
   Small shared bits
--------------------------------------------- */

const StatCard = React.memo(({ label, value, change, icon, currentMonth, previousMonth, getMonthValue, getPrevMonthValue }) => (
  <div className="bg-white rounded-lg border border-[#E4DCE6] p-5 hover:border-[#5B3A60]/40 transition-colors">
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-md bg-[#F0E6F5] flex items-center justify-center">
        <div className="text-[#5B3A60] text-sm">{icon}</div>
      </div>
      <p className={`text-xs font-semibold flex items-center gap-1 ${change?.isPositive ? "text-[#1F9D6C]" : "text-[#C62828]"}`}>
        {change ? (
          <>
            <span>{change.isPositive ? "▲" : "▼"}</span>
            {change.value}%
          </>
        ) : "—"}
      </p>
    </div>
    <p className="text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider mb-1">{label}</p>
    <p className="text-2xl font-bold text-[#3D2542]">{value}</p>
    <p className="text-[11px] text-[#B9A6BC] mt-1.5">
      {new Date().toLocaleString("default", { month: "short" })}: {getMonthValue(currentMonth)}
      {"  ·  "}
      {new Date(new Date().setMonth(new Date().getMonth() - 1)).toLocaleString("default", { month: "short" })}: {getPrevMonthValue(previousMonth)}
    </p>
  </div>
));

const RatingDisplay = ({ value }) => (
  <div className="flex items-center justify-center gap-1">
    <FaStar className="w-3 h-3 text-[#B89D4F]" />
    <span className="text-sm font-semibold text-[#3D2542]">{value ? value.toFixed(1) : "—"}</span>
    {value && <span className="text-[10px] text-[#B9A6BC] font-medium">/5</span>}
  </div>
);

const StatusBadge = ({ active }) =>
  active ? (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#E8F7F0] text-[#1F9D6C] border border-[#BFE8D5] rounded text-[10px] font-semibold">
      <span className="w-1.5 h-1.5 rounded-full bg-[#1F9D6C]" /> LIVE
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#FAF8FB] text-[#7A6B7E] border border-[#E4DCE6] rounded text-[10px] font-semibold">
      INACTIVE
    </span>
  );

/* ---------------------------------------------
   Avatar edit modal
--------------------------------------------- */

const AvatarEditModal = ({ botId, tenantId, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/avatar`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });

      const data = await res.json();
      if (!res.ok) {
        console.error("Upload failed:", data);
        alert("Avatar upload failed");
        return;
      }
      onSuccess();
    } catch (err) {
      console.error("Avatar upload error:", err);
      alert("Something went wrong");
    } finally {
      setUploading(false);
    }
  };

  const handlePresetSelect = async (url) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const file = new File([blob], "avatar.png", { type: blob.type });
      await uploadAvatar(file);
    } catch (err) {
      console.error("Failed to upload preset avatar", err);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadAvatar(file);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-[#3D2542]/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 border border-[#E4DCE6]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[15px] font-bold text-[#3D2542]">Edit Avatar</h3>
          <button onClick={onClose} className="p-1.5 rounded-md text-[#B9A6BC] hover:text-[#3D2542] hover:bg-[#FAF8FB] transition-colors">
            <FaTimes className="text-sm" />
          </button>
        </div>

        {uploading ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
              className="w-8 h-8 border-[3px] border-[#B89D4F] border-t-transparent rounded-full"
            />
            <p className="text-sm text-[#7A6B7E] font-medium">Uploading avatar…</p>
          </div>
        ) : (
          <>
            <p className="text-[11px] font-semibold text-[#7A6B7E] uppercase tracking-wider mb-3">Choose a Style</p>
            <div className="grid grid-cols-5 gap-3 mb-5">
              {DEFAULT_AVATARS.map((a, idx) => (
                <div key={idx} onClick={() => handlePresetSelect(a.url)} className="group flex flex-col items-center cursor-pointer">
                  <div className="w-11 h-11 rounded-md border border-[#E4DCE6] group-hover:border-[#5B3A60] transition-colors overflow-hidden">
                    <img src={a.url} alt={a.label} className="w-full h-full object-cover" />
                  </div>
                  <span className="text-[9px] mt-1.5 text-[#7A6B7E] group-hover:text-[#5B3A60] font-medium text-center transition-colors">{a.label}</span>
                </div>
              ))}
            </div>

            <div className="border-t border-[#E4DCE6] pt-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold border border-[#5B3A60] text-[#5B3A60] rounded-md hover:bg-[#5B3A60] hover:text-white transition-colors"
              >
                <FaUpload className="text-xs" /> Upload Custom
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} />
            </div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

/* ---------------------------------------------
   Agent (bot) card
--------------------------------------------- */

const BotCard = React.memo(({ bot, index, stats, tenantId, avatarErrors, setAvatarErrors, navigate, getBotAvatarUrl, getDomainIcon, openMenuId, setOpenMenuId, setConfirmBotId, isSubscriptionInactive, isAdmin }) => {
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);
  const [avatarKey, setAvatarKey] = useState(Date.now());

  const successRate = stats.success_rate != null ? `${(stats.success_rate * 100).toFixed(0)}%` : "—";
  const ratingValue = stats.conversation_feedback_avg_stars;
  const lastConv = stats.last_conversation_at
    ? new Date(stats.last_conversation_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })
    : "—";

  const handleAvatarSuccess = () => {
    setAvatarErrors((prev) => ({ ...prev, [bot.botid]: false }));
    setAvatarKey(Date.now());
    setShowAvatarEdit(false);
  };

  return (
    <>
      <div
        className={`bg-white rounded-lg border border-[#E4DCE6] overflow-hidden transition-colors flex flex-col
          ${isSubscriptionInactive ? "opacity-60 grayscale cursor-not-allowed" : "hover:border-[#5B3A60]/40"}`}
      >
        <div className="p-4 border-b border-[#E4DCE6] bg-[#FAF8FB] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative group/avatar">
              <div className="relative w-10 h-10 rounded-md bg-white border border-[#E4DCE6] overflow-hidden flex items-center justify-center">
                {!avatarErrors[bot.botid] ? (
                  <img
                    key={avatarKey}
                    src={`${getBotAvatarUrl(bot.botid)}?v=${avatarKey}`}
                    onError={() => setAvatarErrors((prev) => ({ ...prev, [bot.botid]: true }))}
                    className="w-full h-full object-cover"
                    alt={bot.botname}
                  />
                ) : (
                  <div className="p-1.5 text-[#5B3A60]">{getDomainIcon(bot.domain)}</div>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); if (!isSubscriptionInactive) setShowAvatarEdit(true); }}
                className={`absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-full flex items-center justify-center shadow-sm border border-white transition-opacity
                  ${isSubscriptionInactive ? "bg-[#E4DCE6] cursor-not-allowed opacity-0" : "bg-[#5B3A60] text-white opacity-0 group-hover/avatar:opacity-100 hover:bg-[#4A2E50]"}`}
                title="Edit avatar"
              >
                <FaEdit className="text-[7px]" />
              </button>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-[#3D2542] text-sm leading-tight break-words max-w-[180px]" title={bot.botname}>{bot.botname}</p>
                <div className="relative group/botid">
                  <FaInfoCircle className="text-[#B9A6BC] text-[9px] cursor-help hover:text-[#5B3A60] transition-colors" />
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 bg-[#3D2542] text-white text-[9px] rounded whitespace-nowrap opacity-0 invisible group-hover/botid:opacity-100 group-hover/botid:visible transition z-20">
                    {bot.botid}
                  </div>
                </div>
                <StatusBadge active={!isSubscriptionInactive} />
              </div>
              <p className="text-[11px] text-[#7A6B7E] mt-0.5 font-medium">{bot.domain}</p>
            </div>
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-[10px] text-[#B9A6BC] uppercase font-semibold tracking-wide">Last Conversation</p>
            <p className="text-xs font-semibold text-[#3D2542]">{lastConv}</p>
          </div>
        </div>

        <div className="grid grid-cols-4 divide-x divide-[#E4DCE6] bg-white">
          {[
            { label: "Conversations", value: stats.total_conversations || 0 },
            { label: "Users", value: stats.total_users || 0 },
            { label: "Success Rate", value: successRate },
            { label: "Rating", value: ratingValue, isRating: true }
          ].map((item, i) => (
            <div key={i} className="p-3 text-center">
              <p className="text-[10px] text-[#B9A6BC] uppercase font-semibold mb-1 tracking-wide">{item.label}</p>
              {item.isRating ? <RatingDisplay value={item.value} /> : <p className="text-sm font-semibold text-[#3D2542]">{item.value}</p>}
            </div>
          ))}
        </div>

                <div className="mt-auto p-3 bg-[#FAF8FB] border-t border-[#E4DCE6] flex items-center justify-between">
          <div className="flex gap-1.5 flex-wrap">
            {[
              { label: "Overview", path: `/overview/${tenantId}/${bot.botid}` },
              { label: "Knowledge", path: `/knowledgeafteronboard/${tenantId}/${bot.botid}` },
              { label: "Leads", path: `/lead-users/${tenantId}/${bot.botid}` },
              { label: "Settings", path: `/settings/${tenantId}/${bot.botid}` }
            ]
              .filter((action) => {
                if (isAdmin) return true;
                // 🔒 Members cannot see Settings or Knowledge
                return action.label !== "Settings" && action.label !== "Knowledge";
              })
              .map((action) => (
                <button
                  key={action.label}
                  disabled={isSubscriptionInactive}
                  onClick={() => !isSubscriptionInactive && navigate(action.path)}
                  className={`px-2.5 py-1.5 text-[10px] font-semibold rounded border transition-colors
                    ${isSubscriptionInactive
                      ? "text-[#B9A6BC] bg-[#F0E6F5]/40 border-[#E4DCE6] cursor-not-allowed"
                      : "text-[#5B3A60] bg-white border-[#E4DCE6] hover:border-[#5B3A60]"}`}
                >
                  {action.label}
                </button>
              ))}
          </div>
          
          {/* 🔒 Only show the 3-dot menu (Settings/Delete) for Admins */}
          {isAdmin && (
            <div className="relative ml-1.5">
              <button
                onClick={() => setOpenMenuId(openMenuId === bot.botid ? null : bot.botid)}
                className="p-1.5 text-[#7A6B7E] hover:text-[#5B3A60] hover:bg-white rounded-md border border-transparent hover:border-[#E4DCE6] transition-colors"
              >
                <FaEllipsisV className="text-xs" />
              </button>
              <AnimatePresence>
                {openMenuId === bot.botid && (
                  <motion.div
                    initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: 0.1 }}
                    className="absolute right-0 bottom-full mb-1.5 w-36 bg-white rounded-md shadow-lg border border-[#E4DCE6] z-30 overflow-hidden py-1"
                  >
                    <button
                      onClick={() => navigate(`/settings/${tenantId}/${bot.botid}`)}
                      className="w-full text-left px-2.5 py-1.5 text-xs text-[#3D2542] hover:bg-[#F0E6F5] hover:text-[#5B3A60] flex items-center gap-1.5"
                    >
                      <FaCog className="text-[9px]" /> Settings
                    </button>
                    <div className="h-px bg-[#E4DCE6] my-1" />
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmBotId(bot.botid); setOpenMenuId(null); }}
                      className="w-full text-left px-2.5 py-1.5 text-xs text-[#C62828] hover:bg-[#FDEEEE] flex items-center gap-1.5"
                    >
                      <FaTrashAlt className="text-[9px]" /> Delete
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showAvatarEdit && (
          <AvatarEditModal botId={bot.botid} tenantId={tenantId} onClose={() => setShowAvatarEdit(false)} onSuccess={handleAvatarSuccess} />
        )}
      </AnimatePresence>
    </>
  );
});

/* ---------------------------------------------
   Usage & limits card
--------------------------------------------- */

const UsageCard = React.memo(({ usage, isBotLimitReached, formatStorageMB, formatPlanName, subscription_tier }) => (
  <div className="bg-white rounded-lg border border-[#E4DCE6] overflow-hidden sticky top-20">
    <div className="px-6 py-4 border-b border-[#E4DCE6] bg-[#FAF8FB] flex items-center justify-between">
      <h3 className="font-bold text-[#3D2542] text-[15px] flex items-center gap-1.5">
        <FaChartBar className="text-[#5B3A60] text-xs" /> Usage &amp; Limits
      </h3>
      <span className="text-[10px] px-2 py-0.5 rounded bg-[#F0E6F5] text-[#5B3A60] font-semibold border border-[#E4DCE6]">
        {formatPlanName(subscription_tier)}
      </span>
    </div>
    <div className="p-6 space-y-4">
      {[
        { label: "Messages", used: usage.messages.used, limit: usage.messages.limit, color: "bg-[#B89D4F]", text: "text-[#A88D3F]" },
        { label: "Storage", used: usage.storage.used, limit: usage.storage.limit, color: "bg-[#B89D4F]", text: "text-[#A88D3F]", format: formatStorageMB },
        { label: "Agents", used: usage.bots.used, limit: usage.bots.limit, color: isBotLimitReached ? "bg-[#C62828]" : "bg-[#5B3A60]", text: isBotLimitReached ? "text-[#C62828]" : "text-[#5B3A60]", isBots: true }
      ].map((item, i) => (
        <div key={i} className="space-y-1.5">
          <div className="flex justify-between text-[11px]">
            <span className="font-semibold text-[#7A6B7E]">{item.label}</span>
            <span className={`font-semibold ${item.text}`}>
              {item.format ? `${item.format(item.used)} / ${item.format(item.limit)}` : `${item.used.toLocaleString()} / ${item.limit.toLocaleString()}`}
            </span>
          </div>
          <div className="h-1.5 bg-[#F0E6F5] rounded-full overflow-hidden">
            <div
              className={`h-full ${item.color} rounded-full transition-all duration-500`}
              style={{ width: `${item.limit > 0 ? Math.min((item.used / item.limit) * 100, 100) : 0}%` }}
            />
          </div>
          {item.isBots && isBotLimitReached && (
            <p className="mt-1.5 p-1.5 bg-[#FDEEEE] border border-[#F0C7C7] rounded text-[10px] text-[#C62828] font-medium flex items-center gap-1">
              <FaExclamationTriangle className="text-[9px]" /> Limit reached
            </p>
          )}
        </div>
      ))}
    </div>
  </div>
));



/* ---------------------------------------------
   Main dashboard
--------------------------------------------- */

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantId } = useParams();
  const driverRef = useRef(null);
  const [bots, setBots] = useState([]);
  const [botStatsMap, setBotStatsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [avatarErrors, setAvatarErrors] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [confirmBotId, setConfirmBotId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showBotLimitPopover, setShowBotLimitPopover] = useState(false);
  const [overviewLoaded, setOverviewLoaded] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [subscriptionModal, setSubscriptionModal] = useState({ open: false, message: "" });
  const [hasIncompleteWizard, setHasIncompleteWizard] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [latestEscalations, setLatestEscalations] = useState([]);

  const titleRef = useRef(null);
  const createBtnRef = useRef(null);
  const sidebarRef = useRef(null);
  const usageRef = useRef(null);
  const docRef = useRef(null);
  const upgradeRef = useRef(null);
  const metricsGridRef = useRef(null);
  const escalationsRef = useRef(null);
  const userRole = localStorage.getItem("role") || "member";
  const isAdmin = userRole === "admin";

  const formatPlanName = useCallback((tier = "") => {
    if (!tier) return "Free";
    const base = tier.split("_")[0].toLowerCase();
    switch (base) {
      case "starter": return "Starter";
      case "pro": return "Pro";
      case "enterprise": return "Enterprise";
      case "trial": return "Trial";
      case "free": return "Free";
      default: return base.charAt(0).toUpperCase() + base.slice(1);
    }
  }, []);

  const [overview, setOverview] = useState({
    overall: {}, monthly_trend: [], current: {}, previous: {}, total_bots: 0,
    subscription_tier: "FREE", subscription_status: "active", period_end: null,
    access_allowed: true, limits: { bots: 0, messages: 0, storage_mb: 0 }, storage: { mb: 0 }
  });

  const isBotLimitReached = useMemo(
    () => (overview.limits?.bots ?? 0) > 0 && overview.total_bots >= (overview.limits?.bots ?? Infinity),
    [overview.limits?.bots, overview.total_bots]
  );

  const formatLatencySeconds = useCallback((valueMs) => {
    if (valueMs === null || valueMs === undefined) return "—";
    if (valueMs <= 0) return "0 s";
    return `${(valueMs / 1000).toFixed(2)} s`;
  }, []);

  const formatStorageMB = useCallback((value) => {
    if (value === null || value === undefined) return "0 MB";
    if (value >= 1024) return `${(value / 1024).toFixed(2)} GB`;
    return `${value.toFixed(2)} MB`;
  }, []);

  const getBotAvatarUrl = useCallback((botId) => `${getApiBase()}/tenant/${tenantId}/bot/${botId}/avatar`, [tenantId]);

  const getDomainIcon = useCallback((domain = "") => {
    const d = (domain || "").toLowerCase();
    const iconClass = "text-[#5B3A60] text-base";
    if (d.includes("medical") || d.includes("health")) return <FaStethoscope className={iconClass} />;
    if (d.includes("auto") || d.includes("car")) return <FaCar className={iconClass} />;
    if (d.includes("finance") || d.includes("bank")) return <FaMoneyBill className={iconClass} />;
    if (d.includes("education") || d.includes("school")) return <FaGraduationCap className={iconClass} />;
    if (d.includes("travel") || d.includes("tour")) return <FaPlane className={iconClass} />;
    if (d.includes("ecom") || d.includes("shop")) return <FaShoppingCart className={iconClass} />;
    if (d.includes("real estate") || d.includes("property")) return <FaHome className={iconClass} />;
    if (d.includes("food") || d.includes("cafe")) return <FaUtensils className={iconClass} />;
    if (d.includes("fitness") || d.includes("gym")) return <FaDumbbell className={iconClass} />;
    if (d.includes("beauty") || d.includes("salon")) return <FaSpa className={iconClass} />;
    if (d.includes("logistics") || d.includes("delivery")) return <FaTruck className={iconClass} />;
    if (d.includes("law") || d.includes("legal")) return <FaBalanceScale className={iconClass} />;
    if (d.includes("entertainment") || d.includes("media")) return <FaFilm className={iconClass} />;
    if (d.includes("fashion") || d.includes("clothing")) return <FaTshirt className={iconClass} />;
    if (d.includes("agri") || d.includes("farm")) return <FaSeedling className={iconClass} />;
    if (d.includes("game") || d.includes("gaming")) return <FaGamepad className={iconClass} />;
    if (d.includes("hr") || d.includes("recruit")) return <FaUsers className={iconClass} />;
    if (d.includes("insurance") || d.includes("policy")) return <FaShieldAlt className={iconClass} />;
    if (d.includes("telecom") || d.includes("network")) return <FaBroadcastTower className={iconClass} />;
    if (d.includes("crypto") || d.includes("blockchain")) return <FaBitcoin className={iconClass} />;
    if (d.includes("course") || d.includes("learning")) return <FaBook className={iconClass} />;
    if (d.includes("event") || d.includes("ticket")) return <FaTicketAlt className={iconClass} />;
    if (d.includes("photo") || d.includes("camera")) return <FaCamera className={iconClass} />;
    if (d.includes("music") || d.includes("audio")) return <FaMusic className={iconClass} />;
    if (d.includes("manufacturing") || d.includes("factory")) return <FaIndustry className={iconClass} />;
    if (d.includes("energy") || d.includes("solar")) return <FaBolt className={iconClass} />;
    return <FaRobot className={iconClass} />;
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("createBotState");
    if (!saved) { setHasIncompleteWizard(false); return; }
    try {
      const parsed = JSON.parse(saved);
      setHasIncompleteWizard(parsed.step && parsed.step > 1 && !parsed.promptGenerated);
    } catch { setHasIncompleteWizard(false); }
  }, []);

  const fetchTenantOverview = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/overview`);
      const data = await res.json();
      if (data.company_name) {
        localStorage.setItem("company_name", data.company_name);
        setCompanyName(data.company_name);
      } else {
        const existingName = localStorage.getItem("company_name");
        if (existingName && existingName !== "undefined" && existingName !== "null") setCompanyName(existingName);
      }
      setOverview({
        overall: data.overall || {}, monthly_trend: data.monthly_trend || [],
        current: data.current || {}, previous: data.previous || {},
        total_bots: data.total_bots ?? 0, subscription_tier: data.subscription_tier ?? "FREE",
        subscription_status: data.subscription_status ?? "active", period_end: data.period_end ?? null,
        access_allowed: data.access_allowed ?? true,
        limits: { bots: data?.limits?.bots ?? 0, messages: data?.limits?.messages ?? 0, storage_mb: data?.limits?.storage_mb ?? 0 },
        storage: { mb: data?.storage?.mb ?? 0 }
      });
      setOverviewLoaded(true);
    } catch (err) { console.error("Failed to fetch overview:", err); }
  }, [tenantId]);

  const fetchBotStats = useCallback(async (botId) => {
    try {
      const res = await fetch(`${getApiBase()}/bot/${botId}/overview`);
      return await res.json();
    } catch (err) { console.error("Failed to fetch bot stats", err); return null; }
  }, []);

  const fetchBots = useCallback(async () => {
    if (!tenantId) { setBots([]); setLoading(false); return; }
    try {
      logEvent("FETCH_BOTS_START", { tenantId });
      setLoading(true);
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bots`);
      if (!res.ok) { setBots([]); return; }
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      const botsBasic = Array.isArray(data.bots) ? data.bots : [];
      const botsWithDomain = await Promise.all(botsBasic.map(async (bot) => {
        try {
          const detailsRes = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${bot.botid}/details`);
          const detailsText = await detailsRes.text();
          const detailsData = detailsText ? JSON.parse(detailsText) : {};
          const stats = await fetchBotStats(bot.botid);
          setBotStatsMap(prev => ({ ...prev, [bot.botid]: stats }));
          return { ...bot, domain: detailsData?.data?.bot_industry || "-" };
        } catch { return { ...bot, domain: "-" }; }
      }));
      setBots(botsWithDomain);
    } catch (err) { console.error("Fetch bots failed:", err); setBots([]); }
    finally { setLoading(false); }
  }, [tenantId, fetchBotStats]);

  const getBotName = (botId) => {
    const bot = bots.find(b => b.botid === botId);
    return bot ? bot.botname : "Bot";
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const fetchLatestEscalations = useCallback(async () => {
    try {
      const res = await fetch(`${getApiBase()}/api/escalations?tenant_id=${tenantId}`);
      const data = await res.json();
      const sorted = (data.tickets || [])
        .filter(t => t.status !== "resolved")
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setLatestEscalations(sorted);
    } catch (err) { console.error("Escalations fetch failed", err); }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && tenantId !== localStorage.getItem("tenant_id")) localStorage.setItem("tenant_id", tenantId);
  }, [tenantId]);

  useEffect(() => {
    const storedCompany = localStorage.getItem("company_name");
    if (storedCompany && storedCompany !== "undefined" && storedCompany !== "null") setCompanyName(storedCompany);
  }, []);

  useEffect(() => {
    if (tenantId) fetchLatestEscalations();
  }, [tenantId, fetchLatestEscalations]);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const loadDashboard = async () => {
      setOverviewLoaded(false); setBots([]);
      setOverview({
        overall: {}, monthly_trend: [], current: {}, previous: {}, total_bots: 0,
        subscription_tier: "", subscription_status: "active", period_end: null, access_allowed: true,
        limits: { bots: 0, messages: 0, storage_mb: 0 }, storage: { mb: 0 }
      });
      await fetchTenantOverview();
      if (!cancelled) await fetchBots();
    };
    loadDashboard();
    return () => { cancelled = true; };
  }, [tenantId, fetchTenantOverview, fetchBots]);

  useEffect(() => {
    const handleSubscriptionUpdate = () => fetchTenantOverview();
    window.addEventListener("subscriptionUpdated", handleSubscriptionUpdate);
    return () => window.removeEventListener("subscriptionUpdated", handleSubscriptionUpdate);
  }, [fetchTenantOverview]);

  useEffect(() => { logEvent("PAGE_VIEW", { page: "Dashboard" }); }, []);

  useEffect(() => {
    const seen = localStorage.getItem("dashboard_tour_done");
    if (location.pathname.startsWith(`/dashboard`) && !seen && bots.length > 0) {
      setTimeout(() => { startTour(); }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bots, location.pathname]);

  useEffect(() => {
    return () => { if (driverRef.current) driverRef.current.destroy(); };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (window.Userback && typeof window.Userback.setData === "function") {
        window.Userback.setData({ tenant_id: tenantId, company_name: companyName });
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, [tenantId, companyName]);

  const deleteBot = useCallback(async () => {
    if (!confirmBotId) return;
    try {
      setDeleting(true);
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${confirmBotId}`, { method: "DELETE" });
      const data = await res.json();
      if (res.ok) {
        logEvent("BOT_DELETED_SUCCESS", { botId: confirmBotId });
        setToast({ msg: `${data.bot_name} deleted` });
        await fetchTenantOverview(); await fetchBots();
      } else {
        if (res.status === 403) { setSubscriptionModal({ open: true, message: data.detail || "Active subscription required" }); return; }
        setToast({ msg: "Delete failed", type: "error" });
      }
    } catch (err) { logEvent("BOT_DELETED_FAILED", { botId: confirmBotId, error: err.message }); }
    finally { setDeleting(false); setConfirmBotId(null); setOpenMenuId(null); setTimeout(() => setToast(null), 3000); }
  }, [confirmBotId, tenantId, fetchTenantOverview, fetchBots]);

  const startTour = useCallback((tourType = "full") => {
    driverRef.current = driver({
      animate: true,
      opacity: 0.6,
      padding: 12,
      allowClose: true,
      showProgress: true,
      smoothScroll: true,
      overlayColor: "rgba(61, 37, 66, 0.45)",
      popoverClass: "modern-driver-popover",
      nextBtnText: "Next",
      prevBtnText: "Back",
      doneBtnText: "Finish",
      onComplete: () => { localStorage.setItem("dashboard_tour_done", "true"); },
      onDestroyed: () => { if (!localStorage.getItem("dashboard_tour_done")) localStorage.setItem("dashboard_tour_done", "true"); }
    });

    const steps = [];

    if (tourType === "full" || tourType === "sidebar") {
      steps.push({
        element: sidebarRef.current,
        popover: {
          title: "Welcome to the Droog Dashboard",
          description: "Get a centralized view of your workspace and navigate across key sections: Dashboard, Analytics, Billing & Settings.",
          side: "right", align: "start"
        }
      });
    }

    if (tourType === "full" || tourType === "overview") {
      steps.push({
        element: metricsGridRef.current,
        popover: {
          title: "Key Metrics",
          description: "Track your platform activity at a glance. These metrics provide an overview of your conversations, messages, and users.",
          side: "bottom", align: "center"
        }
      });
      steps.push({
        element: createBtnRef.current,
        popover: {
          title: "Create Assistant",
          description: "Build your first agent in just a few steps using the setup wizard.",
          side: "bottom", align: "start"
        }
      });
      steps.push({
        element: titleRef.current,
        popover: {
          title: "Your Agents",
          description: "Manage and access all your agents from one place. Click on any agent to view details or manage its configuration.",
          side: "bottom", align: "start"
        }
      });
    }

    if (tourType === "full" || tourType === "usage") {
      steps.push({
        element: usageRef.current,
        popover: {
          title: "Usage Limits",
          description: "Stay informed about your current usage: message count, storage, and agent limits.",
          side: "left", align: "start"
        }
      });
    }

    if (tourType === "full") {
      steps.push({
        element: escalationsRef.current,
        popover: {
          title: "Latest Escalations",
          description: "Review conversations that need your attention — recent knowledge gaps escalated for human review.",
          side: "left", align: "start"
        }
      });
    }

    steps.push({
      element: docRef.current,
      popover: {
        title: "Documentation",
        description: "Find guidance when you need it. Access documentation to help you navigate the platform.",
        side: "bottom", align: "end"
      }
    });

    steps.push({
      element: upgradeRef.current,
      popover: {
        title: "Upgrade Plan",
        description: "Explore plans that scale with your needs and upgrade based on your usage requirements.",
        side: "bottom", align: "end"
      }
    });

    if (steps.length === 0) return;
    driverRef.current.setSteps(steps);
    driverRef.current.drive();
  }, []);

  const usage = useMemo(() => ({
    messages: { used: overview.current.total_messages ?? 0, limit: overview.limits.messages },
    bots: { used: overview.total_bots ?? 0, limit: overview.limits.bots },
    storage: { used: overview.storage.mb ?? 0, limit: overview.limits.storage_mb ?? 0 }
  }), [overview.current.total_messages, overview.limits.messages, overview.total_bots, overview.limits.bots, overview.storage.mb, overview.limits.storage_mb]);

  const isSubscriptionInactive = useMemo(() => overview.access_allowed === false, [overview.access_allowed]);
  const expiryDate = useMemo(() => overview.period_end ? new Date(overview.period_end) : null, [overview.period_end]);
  const formattedExpiry = useMemo(() => expiryDate ? expiryDate.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }) : "", [expiryDate]);
  const showCancelledBanner = useMemo(() => overview.subscription_status === "cancelled" && overview.access_allowed === true, [overview.subscription_status, overview.access_allowed]);
  const showInactiveBanner = useMemo(() => overview.access_allowed === false, [overview.access_allowed]);

  const getChange = useCallback((current = 0, previous = 0, reverse = false) => {
    if (previous === 0 && current === 0) return { value: "0.0", isPositive: true };
    if (previous === 0) return { value: "100.0", isPositive: true };
    const diff = current - previous;
    const percent = (diff / previous) * 100;
    return { value: Math.abs(percent).toFixed(1), isPositive: reverse ? diff <= 0 : diff >= 0 };
  }, []);

  const monthly = useMemo(() => overview.monthly_trend || [], [overview.monthly_trend]);
  const currentMonth = useMemo(() => monthly[monthly.length - 1] || {}, [monthly]);
  const previousMonth = useMemo(() => monthly[monthly.length - 2] || {}, [monthly]);

  const companyInitial = companyName ? companyName[0].toUpperCase() : "D";

  return (
    <div className="flex min-h-screen bg-[#FAF8FB] text-[#3D2542] font-['Poppins']">

      <Sidebar
  ref={sidebarRef}
  tenantId={tenantId}
  companyName={companyName}
  sidebarOpen={sidebarOpen}
  userRole={localStorage.getItem("role") || "member"}
  disabledItems={
    isSubscriptionInactive
      ? []            // your current logic actually leaves all 4 enabled — see note below
      : []
  }
  onBeforeLogout={() => driverRef.current && driverRef.current.destroy()}
/>

      <main className="flex-1 min-h-screen transition-all duration-200" style={{ marginLeft: sidebarOpen ? 240 : 72 }}>
        <header className="sticky top-0 z-30 bg-white border-b border-[#E4DCE6] px-8 h-16 flex items-center">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-md hover:bg-[#F0E6F5] transition-colors">
                {sidebarOpen ? <FiChevronLeft className="text-[#7A6B7E]" size={18} /> : <FiChevronRight className="text-[#7A6B7E]" size={18} />}
              </button>
              <div className="flex items-center gap-1.5 text-sm">
                <span className="font-medium text-[#7A6B7E]">{companyName || "Workspace"}</span>
                <span className="text-[#C9BDCC]">/</span>
                <span className="font-semibold text-[#3D2542]">Dashboard</span>
              </div>
            </div>
            <HeaderActions
              tenantId={tenantId}
              fullName={localStorage.getItem("fullname")}
              onUpgrade={() => setShowBillingModal(true)}
              docRef={docRef}
              upgradeRef={upgradeRef}
              isAdmin={isAdmin}
            />
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-8 py-8 space-y-6">
          <div className={isSubscriptionInactive ? "opacity-50 grayscale pointer-events-none space-y-6" : "space-y-6"}>

            {showCancelledBanner && (
              <div className="p-4 bg-[#FBF3E4] border-l-2 border-[#B89D4F] rounded-r-md flex items-center justify-between">
                <span className="text-sm text-[#7A5F1F] font-medium">Subscription cancelled. Access ends {formattedExpiry}.</span>
                <button onClick={() => navigate(`/billing/${tenantId}`)} className="text-xs font-bold text-[#7A5F1F] underline decoration-[#B89D4F]/50">
                  Manage billing
                </button>
              </div>
            )}

            {showInactiveBanner && (
              <div className="p-4 bg-[#FDEEEE] border-l-2 border-[#C62828] rounded-r-md flex items-center justify-between">
                <span className="text-sm text-[#C62828] font-medium">Subscription inactive. Assistants are view-only.</span>
                <button onClick={() => navigate(`/billing/${tenantId}`)} className="text-xs font-bold text-[#C62828] underline decoration-[#C62828]/40">
                  Upgrade now
                </button>
              </div>
            )}

            <div>
              <h1 className="text-xl font-bold text-[#3D2542]">Dashboard</h1>
              <p className="text-sm text-[#7A6B7E] mt-1">An overview of your agents, usage, and recent activity.</p>
            </div>

            <div ref={metricsGridRef} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
              <StatCard
                label="Conversations"
                value={(overview.overall?.total_sessions ?? 0).toLocaleString()}
                change={getChange(currentMonth?.total_sessions ?? 0, previousMonth?.total_sessions ?? 0)}
                icon={<FaComments className="w-4 h-4" />}
                currentMonth={currentMonth} previousMonth={previousMonth}
                getMonthValue={(m) => m?.total_sessions ?? 0}
                getPrevMonthValue={(m) => m?.total_sessions ?? 0}
              />
              <StatCard
                label="Messages"
                value={(overview.overall?.total_messages ?? 0).toLocaleString()}
                change={getChange(currentMonth?.total_messages ?? 0, previousMonth?.total_messages ?? 0)}
                icon={<FaPaperPlane className="w-4 h-4" />}
                currentMonth={currentMonth} previousMonth={previousMonth}
                getMonthValue={(m) => m?.total_messages ?? 0}
                getPrevMonthValue={(m) => m?.total_messages ?? 0}
              />
              <StatCard
                label="Total Users"
                value={(overview.overall?.total_users ?? 0).toLocaleString()}
                change={getChange(currentMonth?.total_users ?? 0, previousMonth?.total_users ?? 0)}
                icon={<FaUserFriends className="w-4 h-4" />}
                currentMonth={currentMonth} previousMonth={previousMonth}
                getMonthValue={(m) => m?.total_users ?? 0}
                getPrevMonthValue={(m) => m?.total_users ?? 0}
              />
              <StatCard
                label="Avg Response"
                value={formatLatencySeconds(overview.overall?.average_response_time_ms)}
                change={getChange(currentMonth?.average_response_time_ms ?? 0, previousMonth?.average_response_time_ms ?? 0, true)}
                icon={<FaStopwatch className="w-4 h-4" />}
                currentMonth={currentMonth} previousMonth={previousMonth}
                getMonthValue={(m) => (m?.average_response_time_ms ?? 0).toFixed(2)}
                getPrevMonthValue={(m) => (m?.average_response_time_ms ?? 0).toFixed(2)}
              />
            </div>

            <div className="flex flex-col xl:flex-row gap-6 items-start">
              <div className="flex-1 w-full">
                <div className="flex items-center justify-between mb-5">
                  <h2 ref={titleRef} className="text-[15px] font-bold text-[#3D2542] flex items-center gap-2">
                    Your Agents
                    <span className="text-xs font-normal text-[#7A6B7E] bg-[#F0E6F5] px-2 py-0.5 rounded-full">{bots.length}</span>
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => startTour("full")}
                      className="px-3 py-2 rounded-md border border-[#E4DCE6] text-[#5B3A60] hover:border-[#5B3A60] text-sm font-semibold flex items-center gap-2 transition-colors"
                    >
                      <FaQuestionCircle size={12} /> Guide
                    </button>
                    {isAdmin && (
                    <button
                      ref={createBtnRef}
                      onClick={() => {
                        if (isSubscriptionInactive) return;
                        if (isBotLimitReached) { setShowBotLimitPopover(true); return; }
                        navigate(`/create-bot/${tenantId}`);
                      }}
                      disabled={isSubscriptionInactive}
                      className={`px-4 py-2 rounded-md transition-colors flex items-center gap-2 text-sm font-semibold ${
                        isSubscriptionInactive
                          ? "bg-[#F0E6F5] text-[#B9A6BC] cursor-not-allowed"
                          : isBotLimitReached
                          ? "bg-white text-[#5B3A60] border border-[#5B3A60]"
                          : "bg-[#5B3A60] text-white hover:bg-[#4A2E50]"
                      }`}
                    >
                      <FaPlus size={10} /> Create Agent
                    </button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-4">
                  {loading ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-14 bg-white rounded-lg border border-dashed border-[#E4DCE6]">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 0.8, repeat: Infinity, ease: "linear" }}
                        className="w-8 h-8 border-[3px] border-[#B89D4F] border-t-transparent rounded-full"
                      />
                      <p className="text-sm text-[#7A6B7E] font-medium">Loading agents…</p>
                    </div>
                  ) : bots.length > 0 ? (
                    bots.map((bot, index) => (
                      <BotCard
                        key={bot.botid}
                        bot={bot}
                        index={index}
                        stats={botStatsMap[bot.botid] || {}}
                        tenantId={tenantId}
                        avatarErrors={avatarErrors}
                        setAvatarErrors={setAvatarErrors}
                        navigate={navigate}
                        getBotAvatarUrl={getBotAvatarUrl}
                        getDomainIcon={getDomainIcon}
                        openMenuId={openMenuId}
                        setOpenMenuId={setOpenMenuId}
                        setConfirmBotId={setConfirmBotId}
                        isSubscriptionInactive={isSubscriptionInactive}
                        isAdmin={isAdmin}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12 text-[#B9A6BC] bg-white rounded-lg border border-dashed border-[#E4DCE6]">
                      <FaRobot className="mx-auto text-3xl mb-3 text-[#E4DCE6]" />
                      <p className="text-sm">No agents found. Create one to get started.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full xl:w-80 space-y-4">
                <div ref={usageRef}>
                  <UsageCard
                    usage={usage}
                    isBotLimitReached={isBotLimitReached}
                    formatStorageMB={formatStorageMB}
                    formatPlanName={formatPlanName}
                    subscription_tier={overview.subscription_tier}
                  />
                </div>

                <div ref={escalationsRef} className="bg-white rounded-lg border border-[#E4DCE6] overflow-hidden">
                  <div className="px-6 py-4 border-b border-[#E4DCE6] bg-[#FAF8FB]">
                    <h3 className="text-[15px] font-bold text-[#3D2542] flex items-center gap-1.5">
                      <FaExclamationTriangle className="text-[#B89D4F] text-xs" /> Latest Escalations
                    </h3>
                  </div>
                  <div className="p-3">
                    {latestEscalations.length === 0 ? (
                      <p className="text-[11px] text-[#B9A6BC] italic px-1 py-2">No recent escalations</p>
                    ) : (
                      <div className="divide-y divide-[#E4DCE6]">
                        {latestEscalations.map((e) => {
                          const isToday = new Date(e.created_at).toDateString() === new Date().toDateString();
                          const status = e.escalation_type || "handoff";
                          const statusColor = status === "kb_gap" ? "text-[#A88D3F]" : status === "out_of_scope" ? "text-[#C62828]" : "text-[#5B3A60]";
                          return (
                            <div
                              key={e.escalation_id}
                              onClick={() => navigate(`/leads/${tenantId}/${e.bot_id}`, { state: { escalationId: e.escalation_id, sessionId: e.session_id } })}
                              className="flex items-start gap-2 cursor-pointer group py-2.5 px-1 hover:bg-[#FAF8FB] transition-colors"
                            >
                              <div className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${isToday ? "bg-[#C62828]" : "bg-[#E4DCE6]"}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1">
                                  <p className="text-[11px] text-[#3D2542] font-medium leading-tight line-clamp-2 group-hover:text-[#5B3A60] transition-colors">
                                    "{e.original_query}"
                                  </p>
                                  {isToday && (
                                    <span className="text-[8px] font-bold bg-[#C62828] text-white px-1 py-0.5 rounded shrink-0">NEW</span>
                                  )}
                                </div>
                                <div className="text-[10px] text-[#B9A6BC] mt-1 flex items-center gap-1">
                                  <span className="font-semibold text-[#7A6B7E]">{getBotName(e.bot_id)}</span>
                                  <span>·</span>
                                  <span>{getTimeAgo(e.created_at)}</span>
                                  <span>·</span>
                                  <span className={`font-semibold ${statusColor}`}>{status}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <AnimatePresence>
        {showBotLimitPopover && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#3D2542]/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowBotLimitPopover(false)}>
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white rounded-lg p-5 w-full max-w-sm shadow-xl border border-[#E4DCE6]"
              onClick={e => e.stopPropagation()}>
              <h3 className="text-[15px] font-bold text-[#3D2542] mb-2">Agent limit reached</h3>
              <p className="text-[#7A6B7E] text-sm mb-5">Your current plan allows up to <span className="font-bold text-[#3D2542]">{overview.limits.bots}</span> agents.</p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowBotLimitPopover(false)}
                  className="px-4 py-2 text-[#7A6B7E] font-medium rounded-md hover:bg-[#FAF8FB] transition-colors border border-[#E4DCE6] text-sm">
                  Cancel
                </button>
                <button onClick={() => navigate(`/billing/${tenantId}`)}
                  className="px-4 py-2 bg-[#5B3A60] text-white font-medium rounded-md hover:bg-[#4A2E50] transition-colors text-sm">
                  View Plans
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmBotId && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#3D2542]/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setConfirmBotId(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white w-full max-w-md rounded-lg shadow-xl border border-[#E4DCE6] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-12 h-12 rounded-full bg-[#FDEEEE] flex items-center justify-center border border-[#F0C7C7] mb-4">
                  <FaExclamationTriangle className="text-[#C62828] text-lg" />
                </div>
                <h3 className="text-[16px] font-bold text-[#3D2542] mb-2">Delete Agent?</h3>
                <p className="text-sm text-[#7A6B7E] leading-relaxed px-2">
                  You are about to permanently delete the agent
                  <span className="font-semibold text-[#3D2542]"> "{getBotName(confirmBotId)}" </span>
                  and all its knowledge base data.
                </p>
                <p className="text-sm text-[#C62828] font-medium mt-3">This action cannot be reversed.</p>
                <p className="text-sm text-[#7A6B7E] mt-2">Do you want to proceed?</p>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setConfirmBotId(null)} className="flex-1 px-4 py-2 text-sm rounded-md border border-[#E4DCE6] text-[#7A6B7E] hover:bg-[#FAF8FB] transition-colors">
                  Cancel
                </button>
                <button onClick={deleteBot} disabled={deleting} className="flex-1 px-4 py-2 text-sm rounded-md bg-[#C62828] text-white hover:bg-[#A82121] transition-colors disabled:opacity-60">
                  {deleting ? "Deleting…" : "Delete Agent"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {subscriptionModal.open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#3D2542]/40 backdrop-blur-sm">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="bg-white w-full max-w-md rounded-lg shadow-xl p-5 border border-[#E4DCE6]">
              <h3 className="text-[15px] font-bold text-[#3D2542] mb-2">Subscription Required</h3>
              <p className="text-sm text-[#7A6B7E] mb-5">{subscriptionModal.message}</p>
              <div className="flex justify-end">
                <button onClick={() => setSubscriptionModal({ open: false, message: "" })}
                  className="px-4 py-2 rounded-md border border-[#5B3A60] text-[#5B3A60] hover:bg-[#5B3A60] hover:text-white transition-colors text-sm font-medium">
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showBillingModal && <Billing onClose={() => setShowBillingModal(false)} onSubscriptionSuccess={() => fetchTenantOverview()} />}
      </AnimatePresence>

      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.18 }}
            className={`fixed top-5 right-5 z-50 w-80 px-4 py-3.5 rounded-lg flex items-start gap-3 border shadow-sm bg-white ${
              toast.type === "error" ? "border-l-4 border-l-[#C62828]" : "border-l-4 border-l-[#1F9D6C]"
            } border-t border-r border-b border-[#E4DCE6]`}
            role="alert" aria-live="polite"
          >
            {toast.type === "error" ? (
              <FaExclamationTriangle size={16} className="shrink-0 mt-0.5 text-[#C62828]" />
            ) : (
              <FaCheckCircle size={16} className="shrink-0 mt-0.5 text-[#1F9D6C]" />
            )}
            <span className="text-sm font-medium text-[#3D2542] flex-1">{toast.msg}</span>
            <button
              onClick={() => setToast(null)}
              className="p-1 rounded hover:bg-[#F0E6F5] transition-colors text-[#7A6B7E]"
              aria-label="Close notification"
            >
              <FaTimes size={12} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}